import { AgGridReact } from 'ag-grid-react'
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import { ColDef } from 'ag-grid-community'
import {
  AllTransactionsType,
  CompanyObject,
  TransactionObject,
  Transaction_Type,
} from '../../../../generated/graphql'
import {
  TransactionActionRender,
  AmountInvestedRender,
  DateOfInvestmentRender,
  InvestorNameRender,
  NoOfSharesRender,
  RoundNameRender,
  SecuritiesRender,
  ShareDistinctiveRender,
  TagsRender,
  TransactionTypeRender,
  InvManagerNameRender,
  IsForeignTransactionRender,
  IsDematRender,
  ForeignCurrencyRender,
  TransactionCostRender,
} from './CompanyTransactionAGComponents'
import { KTSVG } from '../../../../_metronic/helpers'
import { GlobalContext } from '../../store/context/globalContext'
import { getCurrency, getSymbolOfCurrency } from '../../../../utils/CurrencyFormatter'
import { useTableStateManagement } from '../../../../utils/TableHook'
import { TableId } from '../../../../utils/TableIdConstants'
import CloudSetting from '../../../../utils/tableStateIcon'

type TransactionProps = {
  data: AllTransactionsType
}

export function CompanyTransactionAGTable(props: TransactionProps) {
  const gridApi = useRef<any>(null)
  let transactionData: TransactionObject[] = props.data.transactionData as TransactionObject[]
  let companyDetails: CompanyObject = props.data.companyData as CompanyObject
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const userCurrencyFormat = getCurrency(userCurrency)

  const [pageLoaded, setPageLoaded] = useState(false)
  const companyId = companyDetails?.hissaCompanyId || companyDetails?.id
  const { filterCondition, setFilterCondition, isSaving, uploadFilter, uploadColumn, tableState } =
    useTableStateManagement(gridApi, TableId.transactionTable + companyId + 'web', pageLoaded)

  const componentsRegistry = useMemo(
    () => ({
      investorName: memo(InvestorNameRender),
      transactionType: memo(TransactionTypeRender),
      roundName: memo(RoundNameRender),
      transactionAmount: memo(AmountInvestedRender),
      security: memo(SecuritiesRender),
      dateOfInvestment: memo(DateOfInvestmentRender),
      // shareDistinctiveNumbers: memo(ShareDistinctiveRender),
      noOfShares: memo(NoOfSharesRender),
      tags: memo(TagsRender),
      isForeignTransaction: memo(IsForeignTransactionRender),
      invManager: memo(InvManagerNameRender),
      foreignTransactionAmount: memo(ForeignCurrencyRender),
      demat: memo(IsDematRender),
      transactionCost: memo(TransactionCostRender),
    }),
    []
  )

  const showNoRowsOverlay = () => (
    <p className='fs-base text-muted text-center'>No Transactions found in this company.</p>
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  )

  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const [filteredRowData, setFilteredRowData] = useState<any>([])
  const [isFilterApplied, setIsFilterApplied] = useState(false)

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent()
    setIsFilterApplied(filtersApplied)

    const filteredData = grid.api.getModel().rowsToDisplay.map((node: any) => node.data)
    setFilteredRowData(filteredData)
    setPinnedBottomRowData(grid)

    uploadFilter()
  }

  useEffect(() => {
    const filterModel = gridApi.current?.api.getFilterModel()

    const filteredData = gridApi.current?.api.getModel().rowsToDisplay.map((node: any) => node.data)

    setFilteredRowData(filteredData)
    gridApi.current?.api.setFilterModel(tableState?.filterState)
  }, [gridApi, tableState?.filterState, tableState?.columnState])

  const openToolPanel = (key: any) => {
    if (key === 'columns') {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key)
        else gridApi?.current?.api?.closeToolPanel()
        setIsColumnOpen((state) => !state)
        setIsFilterOpen(false)
      }
    } else if (key === 'filters') {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key)
        } else gridApi?.current?.api?.closeToolPanel()
        setIsFilterOpen((state) => !state)
        setIsColumnOpen(false)
      }
    }
  }

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Fund / Investor',
        field: 'investorName',
        cellRenderer: 'investorName',
        cellStyle: tableCellStyle,
        // pinned: 'left',
        minWidth: 250,
        sortable: true,
        autoHeight: true,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Transaction Type',
        field: 'transactionType',
        filter: 'agTextColumnFilter',
        cellRenderer: 'transactionType',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Round Name',
        field: 'roundName',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'roundName',
        cellStyle: tableCellStyle,
        initialWidth: 200,
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: `Transaction Amount (${getSymbolOfCurrency(userCurrency)})`,
        field: 'transactionAmount',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'transactionAmount',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipAmountInvested',
      },
      {
        headerName: 'Security',
        field: 'security',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'security',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipUnrealizedValue',
      },
      {
        headerName: 'Transaction Date',
        field: 'dateOfInvestment',
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0
            }
            const dateParts = cellValue.split('-')
            const day = Number(dateParts[2]?.split('T')[0])
            const month = Number(dateParts[1]) - 1
            const year = Number(dateParts[0])
            const cellDate = new Date(year, month, day)
            if (cellDate < dateFromFilter) {
              return -1
            } else if (cellDate > dateFromFilter) {
              return 1
            }
            return 0
          },
        },
        cellRenderer: 'dateOfInvestment',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },

      {
        headerName: 'Number Of Securities',
        field: 'noOfShares',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'noOfShares',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        hide: true,
        tooltipField: 'tooltipNoOfShares',
      },

      {
        headerName: 'Tags',
        field: 'tags',
        filter: 'agTextColumnFilter',
        cellRenderer: 'tags',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Investment Manager',
        field: 'invManager',
        filter: 'agTextColumnFilter',
        cellRenderer: 'invManager',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Demat',
        field: 'demat',
        filter: 'agTextColumnFilter',
        cellRenderer: 'demat',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'FCTRS/FCGPR Filed',
        field: 'isForeignTransaction',
        filter: 'agMultiColumnFilter',
        cellRenderer: 'isForeignTransaction',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
        comparator(valueA, valueB, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ['reset'],
          filters: [
            {
              title: 'FCTRS/FCGPR Status',
              display: 'subMenu',
              buttons: ['reset'],
              filterValueGetter: (params: any) => params.value.isForeignTransaction,
              filterParams: {
                buttons: ['reset'],
                valueGetter: (params: any) =>
                  params.getValue('isForeignTransaction').isForeignTransaction,
              },
            },
            {
              title: 'FCTRS/FCGPR Amount',
              filter: 'agNumberColumnFilter',
              display: 'subMenu',
              buttons: ['reset'],
              filterValueGetter: (params: any) => params.value.foreignTransactionAmount,
              filterParams: {
                buttons: ['reset'],
                valueGetter: (params: any) =>
                  params.getValue('isForeignTransaction').foreignTransactionAmount,
              },
            },
          ],
        },
        tooltipField: 'tooltipForeignTransactionAmount',
      },
      // {
      //   headerName: `FCTRS/FCGPR Amount`,
      //   field: 'foreignTransactionAmount',
      //   filter: 'agNumberColumnFilter',
      //   cellRenderer: 'foreignTransactionAmount',
      //   cellStyle: tableCellStyle,
      //   sortable: true,
      //   minWidth: 175,
      //   hide: true,
      //   menuTabs: ['filterMenuTab'],
      //   tooltipField: 'tooltipForeignTransactionAmount',
      // },
      {
        headerName: '',
        field: 'actions',
        pinned: 'right',
        hide: false,
        width: 60,
        maxWidth: 60,
        filter: false,
        menuTabs: [],
        colId: 'action-column',
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: {
          'padding-left': '9px',
          'padding-right': '4px',
          'padding-top': '3px',
          'line-height': '20px',
        },
        resizable: false,
        sortable: false,
        cellRenderer: TransactionActionRender,
      },
      {
        headerName: `Transaction Cost`,
        field: 'transactionCost',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'transactionCost',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipTransactionCurrency',
      },
    ],
    [userCurrencyFormat]
  )

  const rowData = useMemo(() => {
    setPageLoaded(true)
    return transactionData?.map((transaction: TransactionObject) => {
      const roundName =
        transaction.transactionType === Transaction_Type.Buy
          ? transaction.round
          : // : transaction.transactionType === Transaction_Type.SecondaryBuy
            // ? 'Secondary'
            ''
      const transactionType =
        transaction.transactionType === Transaction_Type.Sell
          ? 'SECONDARY_SELL'
          : transaction.transactionType
      return {
        investorName: transaction.investorsData?.name || transaction.investorName,
        transactionType: transactionType.split('_').join(' '),
        roundName: roundName,
        transactionAmount: Math.abs(transaction.amount),
        tooltipAmountInvested: Math.abs(transaction.amount).toLocaleString(userCurrencyFormat),
        security: transaction.securitySubType,
        dateOfInvestment: transaction.transactionDate,
        noOfShares: transaction.noOfShares,
        tooltipNoOfShares: Math.abs(transaction.noOfShares).toLocaleString(userCurrencyFormat),
        // shareDistinctiveNumbers: '-',
        tags: transaction.tags,
        userCurrencyFormat: userCurrencyFormat,
        userCurrency: userCurrency,
        transactionDetails: transaction,
        companyDetails: companyDetails,
        notes: transaction.notes,
        // isForeignTransaction: transaction.metadata?.isForeignTransaction,
        invManager: transaction.metadata?.investmentManagerName,
        invEmail: transaction.metadata?.email,
        demat: transaction.metadata?.demat,
        foreignTransactionAmount: transaction.metadata?.transactionAmount,
        foreignCurrency: transaction.metadata?.transactionCurrency,
        tooltipForeignTransactionAmount:
          transaction.metadata?.transactionAmount > 0
            ? Math.abs(transaction.metadata?.transactionAmount).toLocaleString(
                getCurrency(transaction.metadata?.transactionCurrency) || userCurrencyFormat
              )
            : '',
        isForeignTransaction: {
          isForeignTransaction: transaction.metadata?.isForeignTransaction,
          foreignTransactionAmount: transaction.metadata?.transactionAmount,
        },
        transactionCost: transaction.metadata?.transactionCost,
        tooltipTransactionCurrency:
          transaction.metadata?.transactionCost > 0
            ? `${Math.abs(transaction.metadata?.transactionCost).toLocaleString(
                getCurrency(transaction.metadata?.transactionCurrency) || userCurrencyFormat
              )}`
            : '',
        isIndirect:
          props.data.indirectTransactionData && props.data.indirectTransactionData.length > 0
            ? true
            : false,
        isLastInvestment: transactionData.length === 1 ? true : false,
      }
    })
  }, [transactionData, userCurrencyFormat])

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode
    const filteredData = data.childrenAfterAggFilter.map((element: any) => element.data)
    const totalInvested = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.transactionAmount,
      0
    )

    // const noOfShares = filteredData?.reduce(
    //   (accumulator: number, data: any) =>
    //     data.transactionType === 'SPLIT CANCEL' ? accumulator : accumulator + data.noOfShares,
    //   0
    // )
    api.setPinnedBottomRowData([
      {
        investorName: 'Total',
        transactionType: null,
        roundName: null,
        transactionAmount: Math.abs(totalInvested),
        tooltipAmountInvested: Math.abs(totalInvested).toLocaleString(userCurrencyFormat),
        security: null,
        dateOfInvestment: null,
        noOfShares: null,
        tooltipNoOfShares: null,
        tags: null,
        userCurrencyFormat: userCurrencyFormat,
        userCurrency: userCurrency,
        transactionDetails: null,
        companyDetails: companyDetails,
        isIndirect: null,
        isLastInvestment: null,
        isForeignTransaction: {
          isForeignTransaction: '',
          foreignTransactionAmount: 0,
        },
      },
    ])
  }

  return (
    <div
      className='card p-0 h-600px overflow-auto'
      style={{
        boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
        border: '1',
        borderColor: '#F1F1F2',
      }}
    >
      <div className='d-flex flex-row border-0 p-9'>
        <div className='card-title align-items-end flex-column'>
          <h1 className='m-0'>My Transactions</h1>
          <span className='text-muted'>
            {transactionData.length > 0 ? `${transactionData.length} Transactions` : ''}
          </span>
        </div>
        <div className='col d-flex flex-row justify-content-end'>
          <CloudSetting
            tableId={TableId.transactionTable + companyId + 'web'}
            isLoading={isSaving}
            filterCondition={filterCondition}
            setFilterCondition={setFilterCondition}
          />
          <button
            type='submit'
            value={'Columns'}
            onClick={() => openToolPanel('columns')}
            className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal text-hover-primary'
          >
            <KTSVG
              path='/media/icons/duotune/layouts/lay004.svg'
              className='svg-icon-3 text-secondary-txt'
            />{' '}
            Columns
          </button>
          <button
            value={'Filters'}
            onClick={() => openToolPanel('filters')}
            className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal ms-4 text-hover-primary'
          >
            <KTSVG
              path='/media/icons/duotune/general/gen031.svg'
              className='svg-icon-3 text-secondary-txt'
            />{' '}
            Filter
          </button>
        </div>
      </div>
      <div className='card-body w-100 p-9 pt-0 overflow-auto ag-theme-material h-100'>
        <AgGridReact
          sideBar={agConfigSideBar}
          onGridReady={(params) => {
            gridApi.current = params
          }}
          getRowId={(params) => `${params.data.investorName}-${params.data.transactionType}-${params.data.dateOfInvestment}-${params.data.transactionAmount}`}
          components={componentsRegistry}
          alwaysShowHorizontalScroll
          alwaysMultiSort
          animateRows={true}
          defaultColDef={defaultColDef}
          onFilterChanged={onAgGridFilterChanged}
          onColumnMoved={uploadColumn}
          onColumnEverythingChanged={uploadColumn}
          onColumnResized={uploadColumn}
          onRowDataUpdated={setPinnedBottomRowData}
          rowData={rowData}
          columnDefs={columnDefs}
          pagination={false}
          enableCharts={true}
          enableRangeSelection={true}
          suppressRowTransform={true}
          suppressCopyRowsToClipboard={true}
          suppressCopySingleCellRanges={true}
          suppressCellFocus={true}
          noRowsOverlayComponent={showNoRowsOverlay}
          suppressMenuHide={false}
          tooltipShowDelay={50}
          tooltipInteraction={true}
          rowClass={'border-right-0 border-left-0 border-dashed'}
          overlayNoRowsTemplate={
            '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
          }
          getRowStyle={(params) => {
            if (params.rowIndex % 2 === 0) {
              return { background: '#f8f8f8' }
            } else {
              return { background: '#ffffff' }
            }
          }}
        ></AgGridReact>
      </div>
    </div>
  )
}
